import graphql__9030171df55cdf2ba8a8 from "./__generated__/EditSubscriptionAmountOverrideDialogSubscriptionsQuery.graphql.ts";
import graphql__d5aa70c9c8f1e3cba5fd from "./__generated__/EditSubscriptionAmountOverrideDialogMutation.graphql.ts";import * as React from "react";
import {fetchQuery, graphql} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import {useIntl} from "react-intl";
import * as yup from "yup";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import FormDialog from "./FormDialog";
import Section from "../form/Section";
import Row from "../form/Row";
import Label from "../form/Label";
import Field from "../form/Field";
import asFormikField from "../abstract/asFormikField";
import AbstractCombobox from "../abstract/AbstractCombobox";
import {commitMutationWithErrorHandling} from "../../helper/commitMutationWithErrorHandling";
import {formatCentsForInput, parseAmountToCents, yupAmount} from "../../helper/amountInput";
import {useErrorContext} from "../../contexts/ErrorContext";
import type {EditSubscriptionAmountOverrideDialogMutation} from "./__generated__/EditSubscriptionAmountOverrideDialogMutation.graphql";

type SubscriptionEntry = {
    id: string
    customerName: string
    label: string
}

type Props = {
    // Without an override, the dialog sets one for a subscription picked in the dialog
    override?: {
        id: string
        monthlyNetAmount: number
        note?: string | null
        subscription?: {
            subscriptionServiceReferenceLabel?: string | null
            customerAccount: {
                customerName: string
            }
        } | null
    }
    onClose: (changed: boolean) => void
}

// The list for an empty search term is loaded at once, only typing is debounced
const subscriptionsDataProvider = (relayEnvironment) => {
    const search = (searchTerm: string = ''): Promise<SubscriptionEntry[]> => {
        return fetchQuery(relayEnvironment, graphql__9030171df55cdf2ba8a8, {searchTerm}).toPromise().then((data: any) => {
            const nodes = data?.subscriptions?.edges?.map(edge => edge.node) ?? [];
            return nodes
                .filter(node => node.state !== 'EXPIRED')
                .map(node => ({id: node.id, customerName: node.customerAccount.customerName, label: node.subscriptionServiceReferenceLabel ?? node.id}));
        });
    };
    const debouncedSearch = AwesomeDebouncePromise(search, 400);
    return (searchTerm: string = ''): Promise<SubscriptionEntry[]> => searchTerm.trim() === '' ? search('') : debouncedSearch(searchTerm.trim());
};

const SubscriptionCombobox = asFormikField(AbstractCombobox, 'subscription');

const setMutation = graphql__d5aa70c9c8f1e3cba5fd;

/**
 * Sets the monthly net amount the income forecast uses for a subscription. The subscription is
 * picked by searching for its customer or label, and cannot be changed for an existing amount.
 */
const EditSubscriptionAmountOverrideDialog: React.FC<Props> = ({override, onClose}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();
    const dataProvider = React.useMemo(() => subscriptionsDataProvider(relayEnvironment), [relayEnvironment]);

    const handleSubmission = (values) => {
        commitMutationWithErrorHandling<EditSubscriptionAmountOverrideDialogMutation>(
            relayEnvironment,
            {
                mutation: setMutation,
                variables: {
                    input: {
                        id: override ? override.id : values.subscription.id,
                        monthlyNetAmount: parseAmountToCents(values.monthlyNetAmount) as number,
                        note: values.note.trim() === '' ? null : values.note.trim(),
                    },
                },
                onCompleted: (_, errors) => {
                    if (errors && errors.length > 0) {
                        console.error(errors);
                    }
                    onClose(true);
                },
                onError: (error) => {
                    console.error(error);
                    onClose(false);
                },
            },
            addError,
            {customErrorMessage: intl.formatMessage({id: 'errors.failedToSetSubscriptionAmountOverride'})}
        );
    };

    const validationSchema = yup.object().shape({
        ...(override ? {} : {subscription: yup.object().required(intl.formatMessage({id: 'validation.field.required'}))}),
        monthlyNetAmount: yupAmount(intl, {nonNegative: true}),
        note: yup.string().max(255, intl.formatMessage({id: 'validation.field.noteTooLong'})),
    });

    return (
        <FormDialog handleCloseDialog={() => onClose(false)}
                    handleSubmission={handleSubmission}
                    initialValues={{
                        monthlyNetAmount: override ? formatCentsForInput(override.monthlyNetAmount) : '',
                        note: override?.note ?? '',
                    }}
                    validationSchema={validationSchema}
                    headline={intl.formatMessage({id: override ? 'dialog.EditSubscriptionAmountOverrideDialog.editHeadline' : 'dialog.EditSubscriptionAmountOverrideDialog.addHeadline'})}
                    submitLabel={intl.formatMessage({id: 'button.save'})}>
            <Section>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditSubscriptionAmountOverrideDialog.subscriptionLabel'})}</Label>
                    {override ? (
                        <p className="text-sm text-gray-900">
                            {override.subscription
                                ? override.subscription.customerAccount.customerName + ' · ' + (override.subscription.subscriptionServiceReferenceLabel ?? override.id)
                                : override.id}
                        </p>
                    ) : (
                        <SubscriptionCombobox
                            getEntryId={(entry: SubscriptionEntry) => entry.id}
                            getEntryLabel={(entry: SubscriptionEntry | null) => entry ? entry.customerName : ''}
                            getEntrySecondaryLabel={(entry: SubscriptionEntry | null) => entry?.label}
                            dataProvider={dataProvider}
                            autoFocus={true}
                        />
                    )}
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditSubscriptionAmountOverrideDialog.monthlyNetAmountLabel'})}</Label>
                    <Field name="monthlyNetAmount" type="text" inputMode="decimal" placeholder="0,00" autoFocus={override !== undefined} data-1p-ignore="true"/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditSubscriptionAmountOverrideDialog.noteLabel'})}</Label>
                    <Field name="note" type="text" data-1p-ignore="true"/>
                </Row>
                <p className="text-sm text-gray-500">
                    {intl.formatMessage({id: 'dialog.EditSubscriptionAmountOverrideDialog.description'})}
                </p>
            </Section>
        </FormDialog>
    );
};

export default EditSubscriptionAmountOverrideDialog;
